import { useState, useEffect } from "react";
import { Route, Routes, useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Space, Breadcrumb, Card, Button, Menu, message } from "antd";
import { PageHeader } from "@ant-design/pro-layout";

import { Site, ZoneConfig } from "../../model/model";
import SessionStore from "../../model/SessionStore";
import DeleteConfirm from "../../components/DeleteConfirm";
import ZoneForm from "./ZoneForm";
import ZoneSwitches from "./ZoneSwitches";

interface IProps {
  site: Site;
}

function ZoneLayout(props: IProps) {
  const { zoneId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [zoneConfig, setZoneConfig] = useState<ZoneConfig | undefined>(undefined);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (!zoneId) return;
    SessionStore.getClient()
      .getZoneConfig(zoneId, SessionStore.getBearer())
      .then((resp) => setZoneConfig(resp.data));
  }, [zoneId]);

  const deleteZone = () => {
    if (!zoneId) return;
    SessionStore.getClient()
      .removeZoneConfig(zoneId, SessionStore.getBearer())
      .then(() => navigate(`/site/${props.site.id}/zones`))
      .catch((err) => {
        messageApi.error(err.message);
      });
  };

  const onFinish = (obj: ZoneConfig) => {
    SessionStore.getClient()
      .updateZoneConfig(obj, SessionStore.getBearer())
      .then(() => {
        messageApi.success("Zone config updated");
        setZoneConfig(obj);
      })
      .catch((err) => messageApi.error(err.message));
  };

  if (!zoneConfig) return null;

  const path = location.pathname;
  let tab = "edit";
  if (path.endsWith("/switches")) {
    tab = "switches";
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="large">
      {contextHolder}
      <PageHeader
        breadcrumbRender={() => (
          <Breadcrumb>
            <Breadcrumb.Item>Sites</Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/site/${props.site.id}/dash`}>{props.site.name}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/site/${props.site.id}/zones`}>Zone Configs</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{zoneConfig.name}</Breadcrumb.Item>
          </Breadcrumb>
        )}
        title={zoneConfig.name}
        subTitle={`id: ${zoneConfig.id}`}
        extra={[
          <DeleteConfirm key="del" typ="zone config" confirm={zoneConfig.id} onConfirm={deleteZone}>
            <Button danger type="primary">
              Delete zone config
            </Button>
          </DeleteConfirm>,
        ]}
      />
      <Card>
        <Menu mode="horizontal" selectedKeys={[tab]} style={{ marginBottom: 24 }}>
          <Menu.Item key="edit">
            <Link to={`/site/${props.site.id}/zone/${zoneConfig.id}`}>Edit</Link>
          </Menu.Item>
          <Menu.Item key="switches">
            <Link to={`/site/${props.site.id}/zone/${zoneConfig.id}/switches`}>Switches</Link>
          </Menu.Item>
        </Menu>
        <Routes>
          <Route path="/" element={<ZoneForm initialValues={zoneConfig} site={props.site} onFinish={onFinish} update />} />
          <Route path="/switches" element={<ZoneSwitches zoneConfig={zoneConfig} />} />
        </Routes>
      </Card>
    </Space>
  );
}

export default ZoneLayout;
